import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../context/ProductStore';
import {
  Upload, X, Star, GripVertical, Image,
  ChevronDown, Save, Eye, ArrowLeft, Info,
  Tag, Globe, Package, AlertCircle
} from 'lucide-react';
import { uploadImage } from '../utils/upload';
import './ProductForm.css';

const EMPTY = {
  name: '', shortDesc: '', description: '',
  price: '', mrp: '', category: '', subcat: '', sku: '',
  status: 'published', stock: 'instock',
  offerStart: '', offerEnd: '',
  metaTitle: '', metaDesc: '', slug: '', altTag: '',
  highlights: ['', '', ''],
  images: [],
  variants: [
    { weight: '250g', price: '', mrp: '', stock: 'instock' },
    { weight: '500g', price: '', mrp: '', stock: 'instock' },
    { weight: '1Kg (Pack of 2)', price: '', mrp: '', stock: 'instock' }
  ]
};

function calcDiscount(price, mrp) {
  if (!price || !mrp || mrp <= price) return 0;
  return Math.round(((mrp - price) / mrp) * 100);
}

function genSlug(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function ProductForm({ existing }) {
  const { addProduct, updateProduct, getFullUrl, categories = [], subcategories = {} } = useStore();
  const navigate = useNavigate();
  const fileRef = useRef();

  const [form, setForm] = useState(existing ? {
    ...EMPTY, ...existing,
    highlights: existing.highlights?.length ? existing.highlights : ['','',''],
    images: existing.images || (existing.image ? [{ url: existing.image, featured: true, alt: existing.name }] : []),
  } : { ...EMPTY, sku: `FLY-${Date.now().toString().slice(-4)}` });

  const [dragging, setDragging] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [errors,   setErrors]   = useState({});
  const [activeTab, setActiveTab] = useState('general');

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Auto-fill slug from name
  const handleNameChange = (val) => {
    set('name', val);
    if (!form.slug || form.slug === genSlug(form.name)) set('slug', genSlug(val));
    if (!form.metaTitle || form.metaTitle === form.name + ' | KOA') set('metaTitle', val + ' | KOA');
  };

  const discount = calcDiscount(Number(form.price), Number(form.mrp));

  // ── Image handling ──
  const processFiles = async (files) => {
    console.log("Processing files:", files.length);
    const arr = Array.from(files).filter(f => f.type.startsWith('image/'));
    for (const file of arr) {
      try {
        console.log("Uploading file:", file.name);
        const url = await uploadImage(file);
        console.log("File uploaded successfully, URL:", url);
        setForm(f => ({
          ...f,
          images: [
            ...f.images,
            { url, featured: f.images.length === 0, alt: form.name || file.name }
          ]
        }));
      } catch (err) {
        console.error("Upload error for file:", file.name, err);
      }
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    processFiles(e.dataTransfer.files);
  }, [processFiles]);

  const removeImage = (idx) => {
    setForm(f => {
      const imgs = f.images.filter((_, i) => i !== idx);
      if (imgs.length > 0 && !imgs.some(im => im.featured)) imgs[0].featured = true;
      return { ...f, images: imgs };
    });
  };

  const setFeatured = (idx) => {
    setForm(f => ({ ...f, images: f.images.map((im, i) => ({ ...im, featured: i === idx })) }));
  };

  const updateAlt = (idx, alt) => {
    setForm(f => ({ ...f, images: f.images.map((im, i) => i === idx ? { ...im, alt } : im) }));
  };

  // Drag-sort images
  const [dragImg, setDragImg] = useState(null);
  const onImgDragStart = (i) => setDragImg(i);
  const onImgDragOver  = (e, i) => {
    e.preventDefault();
    if (dragImg === null || dragImg === i) return;
    setForm(f => {
      const imgs = [...f.images];
      const [moved] = imgs.splice(dragImg, 1);
      imgs.splice(i, 0, moved);
      setDragImg(i);
      return { ...f, images: imgs };
    });
  };

  // ── Validation ──
  const validate = () => {
    const e = {};
    if (!form.name.trim())        e.name     = 'Product name is required';
    if (!form.price)              e.price    = 'Price is required';
    if (!form.mrp)                e.mrp      = 'MRP is required';
    if (!form.category)           e.category = 'Please select a category';
    if (Number(form.price) > Number(form.mrp)) e.price = 'Price cannot be more than MRP';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validate()) { setActiveTab('general'); return; }
    setSaving(true);
    await new Promise(r => setTimeout(r, 500));
    const firstVariantPrice = form.variants?.[0]?.price;
    const firstVariantMrp = form.variants?.[0]?.mrp;
    const finalPrice = firstVariantPrice ? Number(firstVariantPrice) : Number(form.price);
    const finalMrp = (firstVariantMrp != null && firstVariantMrp !== '') ? Number(firstVariantMrp) : Number(form.mrp);

    const data = {
      ...form,
      price:    finalPrice,
      mrp:      finalMrp,
      rating:   form.rating   || 4.5,
      reviews:  form.reviews  || 0,
      image:    form.images?.[0]?.url || form.image || '',
      highlights: form.highlights.filter(Boolean),
    };
    try {
      if (existing) { await updateProduct(existing.id, data); }
      else          { await addProduct(data); }
      setSaving(false);
      setSaved(true);
      setTimeout(() => navigate('/admin/products'), 1200);
    } catch (err) {
      console.error(err);
      alert('Failed to save product: ' + err.message);
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'general', label: 'General',    icon: <Package size={14} /> },
    { id: 'variants', label: 'Variants & Prices', icon: <Tag size={14} /> },
    { id: 'images',  label: 'Images',     icon: <Image size={14} /> },
    { id: 'offers',  label: 'Offers',     icon: <Tag size={14} /> },
    { id: 'seo',     label: 'SEO',        icon: <Globe size={14} /> },
  ];

  const activeSubs = subcategories[form.category] || [];

  return (
    <div className="product-form-page">
      {/* Header */}
      <div className="admin-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={() => navigate('/admin/products')} className="admin-btn admin-btn-secondary admin-btn-sm">
            <ArrowLeft size={14} />
          </button>
          <div>
            <h1 className="admin-page-title">{existing ? 'Edit Product' : 'Add New Product'}</h1>
            <p className="admin-page-sub">{existing ? `Editing: ${existing.name}` : 'Fill in the details to add a new product'}</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button type="button" className="admin-btn admin-btn-secondary" onClick={() => window.open(`/product/${form.slug}`, '_blank')}>
            <Eye size={15} /> Preview
          </button>
          <button type="submit" form="product-form" className="admin-btn admin-btn-primary" disabled={saving}>
            {saving ? '⏳ Saving...' : saved ? '✓ Saved!' : <><Save size={15} /> Save Product</>}
          </button>
        </div>
      </div>

      <form id="product-form" onSubmit={handleSave} noValidate>
        <div className="pf-layout">
          {/* Main column */}
          <div className="pf-main">
            {/* Tabs */}
            <div className="pf-tabs">
              {tabs.map(t => (
                <button
                  key={t.id} type="button"
                  className={`pf-tab ${activeTab === t.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(t.id)}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {/* ── GENERAL TAB ── */}
            {activeTab === 'general' && (
              <div className="admin-card pf-section">
                <div className="pf-field">
                  <label className="pf-label">Product Name *</label>
                  <input
                    className={`pf-input ${errors.name ? 'error' : ''}`}
                    value={form.name}
                    onChange={e => handleNameChange(e.target.value)}
                    placeholder="e.g. Premium Kashmiri Almonds"
                  />
                  {errors.name && <p className="pf-error"><AlertCircle size={12} /> {errors.name}</p>}
                </div>

                <div className="pf-field">
                  <label className="pf-label">Short Description</label>
                  <input
                    className="pf-input"
                    value={form.shortDesc}
                    onChange={e => set('shortDesc', e.target.value)}
                    placeholder="Brief one-line description shown on product card"
                    maxLength={120}
                  />
                  <span className="pf-hint">{form.shortDesc.length}/120 characters</span>
                </div>

                <div className="pf-field">
                  <label className="pf-label">Full Description</label>
                  <textarea
                    className="pf-input pf-textarea"
                    value={form.description}
                    onChange={e => set('description', e.target.value)}
                    placeholder="Detailed product description..."
                    rows={5}
                  />
                </div>

                <div className="pf-field">
                  <label className="pf-label">Key Highlights <span className="pf-hint-inline">(shown as bullet points)</span></label>
                  {form.highlights.map((h, i) => (
                    <input
                      key={i}
                      className="pf-input"
                      style={{ marginBottom: '8px' }}
                      value={h}
                      onChange={e => {
                        const hs = [...form.highlights];
                        hs[i] = e.target.value;
                        set('highlights', hs);
                      }}
                      placeholder={`Highlight ${i+1} (e.g. Rich in Protein)`}
                    />
                  ))}
                  <button type="button" className="pf-add-btn" onClick={() => set('highlights', [...form.highlights, ''])}>
                    + Add Highlight
                  </button>
                </div>

                <div className="pf-row">
                  <div className="pf-field">
                    <label className="pf-label">Category *</label>
                    <div className="pf-select-wrap">
                      <select
                        className={`pf-input pf-select ${errors.category ? 'error' : ''}`}
                        value={form.category}
                        onChange={e => { set('category', e.target.value); set('subcat', ''); }}
                      >
                        <option value="">Select Category</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.emoji} {c.name}</option>)}
                      </select>
                      <ChevronDown size={14} className="pf-select-icon" />
                    </div>
                    {errors.category && <p className="pf-error"><AlertCircle size={12} /> {errors.category}</p>}
                  </div>
                  <div className="pf-field">
                    <label className="pf-label">Subcategory</label>
                    <div className="pf-select-wrap">
                      <select
                        className="pf-input pf-select"
                        value={form.subcat}
                        onChange={e => set('subcat', e.target.value)}
                        disabled={!activeSubs.length}
                      >
                        <option value="">All {form.category || 'Category'}</option>
                        {activeSubs.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                      <ChevronDown size={14} className="pf-select-icon" />
                    </div>
                  </div>
                </div>

                <div className="pf-row">
                  <div className="pf-field">
                    <label className="pf-label">SKU / Product Code</label>
                    <input className="pf-input" value={form.sku} onChange={e => set('sku', e.target.value)} placeholder="FLY-0001" />
                  </div>
                  <div className="pf-field">
                    <label className="pf-label">Weight / Quantity</label>
                    <input className="pf-input" value={form.weight} onChange={e => set('weight', e.target.value)} placeholder="e.g. 250g, 500g" />
                    <span className="pf-hint">Use comma for multiple options (e.g. 250g, 500g)</span>
                  </div>
                </div>

                <div className="pf-row">
                  <div className="pf-field">
                    <label className="pf-label">Status</label>
                    <div className="pf-select-wrap">
                      <select className="pf-input pf-select" value={form.status} onChange={e => set('status', e.target.value)}>
                        <option value="published">✅ Published</option>
                        <option value="draft">📝 Draft</option>
                      </select>
                      <ChevronDown size={14} className="pf-select-icon" />
                    </div>
                  </div>
                  <div className="pf-field">
                    <label className="pf-label">Stock Status</label>
                    <div className="pf-select-wrap">
                      <select className="pf-input pf-select" value={form.stock} onChange={e => set('stock', e.target.value)}>
                        <option value="instock">📦 In Stock</option>
                        <option value="outofstock">❌ Out of Stock</option>
                      </select>
                      <ChevronDown size={14} className="pf-select-icon" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* ── VARIANTS TAB ── */}
            {activeTab === 'variants' && (
              <div className="admin-card pf-section">
                <h3 className="pf-section-title">Product Variants (Weights & Prices)</h3>
                <p className="pf-section-sub">Set different prices for different weights. If variants are set, these prices will be used on the product page.</p>

                <div className="variants-list">
                  <div className="variant-header-row">
                    <span className="v-col">Weight</span>
                    <span className="v-col">MRP (₹)</span>
                    <span className="v-col">Sale Price (₹)</span>
                    <span className="v-col">Stock Status</span>
                    <span className="v-col"></span>
                  </div>
                  
                  {(form.variants || []).map((v, i) => (
                    <div key={i} className="variant-row">
                      <div className="v-col">
                        <input 
                          className="pf-input" 
                          value={v.weight} 
                          onChange={e => {
                            const vs = [...form.variants];
                            vs[i].weight = e.target.value;
                            set('variants', vs);
                          }}
                          placeholder="e.g. 500g"
                        />
                      </div>
                      <div className="v-col">
                        <input 
                          className="pf-input" 
                          type="number"
                          value={v.mrp} 
                          onChange={e => {
                            const vs = [...form.variants];
                            vs[i].mrp = e.target.value;
                            setForm(f => ({
                              ...f,
                              variants: vs,
                              ...(i === 0 && e.target.value ? { mrp: e.target.value } : {})
                            }));
                          }}
                          placeholder="0"
                        />
                      </div>
                      <div className="v-col">
                        <input 
                          className="pf-input" 
                          type="number"
                          value={v.price} 
                          onChange={e => {
                            const vs = [...form.variants];
                            vs[i].price = e.target.value;
                            setForm(f => ({
                              ...f,
                              variants: vs,
                              ...(i === 0 && e.target.value ? { price: e.target.value } : {})
                            }));
                          }}
                          placeholder="0"
                        />
                      </div>
                      <div className="v-col">
                        <select 
                          className="pf-input pf-select" 
                          value={v.stock}
                          onChange={e => {
                            const vs = [...form.variants];
                            vs[i].stock = e.target.value;
                            set('variants', vs);
                          }}
                        >
                          <option value="instock">📦 In Stock</option>
                          <option value="outofstock">❌ Out of Stock</option>
                        </select>
                      </div>
                      <div className="v-col" style={{ flex: '0 0 40px' }}>
                        <button type="button" className="pf-img-btn pf-img-btn-delete" onClick={() => {
                          const vs = form.variants.filter((_, idx) => idx !== i);
                          set('variants', vs);
                        }}>
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  <button type="button" className="pf-add-btn" style={{ marginTop: '12px' }} onClick={() => {
                    const vs = [...(form.variants || []), { weight: '', price: '', mrp: '', stock: 'instock' }];
                    set('variants', vs);
                  }}>
                    + Add New Variant
                  </button>
                </div>

                <div className="pf-info-box" style={{ marginTop: 24 }}>
                  <Info size={16} />
                  <p><strong>Note:</strong> Regular Price and Sale Price in the 'General' tab will be used as default if no variants are selected by the customer.</p>
                </div>
              </div>
            )}

            {/* ── IMAGES TAB ── */}
            {activeTab === 'images' && (
              <div className="admin-card pf-section">
                <h3 className="pf-section-title">Product Images</h3>
                <p className="pf-section-sub">Upload multiple images. Drag to reorder. Click ⭐ to set featured image.</p>

                {/* Drop zone */}
                <div
                  className={`pf-dropzone ${dragging ? 'dragging' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  onClick={() => fileRef.current?.click()}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => processFiles(e.target.files)}
                  />
                  <div className="pf-dropzone-inner">
                    <Upload size={32} className="pf-dropzone-icon" />
                    <p className="pf-dropzone-title">Drag & drop images here</p>
                    <p className="pf-dropzone-sub">or click to browse — JPG, PNG, WEBP supported</p>
                  </div>
                </div>

                {/* Image grid */}
                {form.images.length > 0 && (
                  <div className="pf-images-grid">
                    {form.images.map((img, idx) => (
                      <div
                        key={idx}
                        className={`pf-img-card ${img.featured ? 'featured' : ''}`}
                        draggable
                        onDragStart={() => onImgDragStart(idx)}
                        onDragOver={e => onImgDragOver(e, idx)}
                      >
                        <div className="pf-img-wrap">
                          <img src={getFullUrl(img.url)} alt={img.alt} className="pf-img-preview" />
                          {img.featured && <span className="pf-featured-badge">Featured</span>}
                          <div className="pf-img-actions">
                            <button type="button" className="pf-img-btn" title="Set as Featured" onClick={() => setFeatured(idx)}>
                              <Star size={13} fill={img.featured ? 'currentColor' : 'none'} />
                            </button>
                            <button type="button" className="pf-img-btn pf-img-btn-drag" title="Drag to reorder">
                              <GripVertical size={13} />
                            </button>
                            <button type="button" className="pf-img-btn pf-img-btn-delete" title="Remove" onClick={() => removeImage(idx)}>
                              <X size={13} />
                            </button>
                          </div>
                        </div>
                        <div className="pf-img-alt-wrap">
                          <input
                            className="pf-img-alt-input"
                            value={img.alt}
                            onChange={e => updateAlt(idx, e.target.value)}
                            placeholder="Image alt tag (for SEO)..."
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="pf-field" style={{ marginTop: '20px' }}>
                  <label className="pf-label"><Info size={13} style={{ display: 'inline', marginRight: 4 }} /> Or paste an image URL directly</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      className="pf-input"
                      placeholder="https://images.unsplash.com/..."
                      id="img-url-input"
                    />
                    <button
                      type="button"
                      className="admin-btn admin-btn-secondary"
                      onClick={() => {
                        const url = document.getElementById('img-url-input').value.trim();
                        if (url) {
                          setForm(f => ({
                            ...f,
                            images: [...f.images, { url, featured: f.images.length === 0, alt: form.name }]
                          }));
                          document.getElementById('img-url-input').value = '';
                        }
                      }}
                    >
                      Add URL
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── OFFERS TAB ── */}
            {activeTab === 'offers' && (
              <div className="admin-card pf-section">
                <h3 className="pf-section-title">Pricing & Offers</h3>

                <div className="pf-row">
                  <div className="pf-field">
                    <label className="pf-label">Regular Price (MRP) *</label>
                    <div className="pf-input-prefix">
                      <span className="pf-prefix">₹</span>
                      <input
                        className={`pf-input pf-input-with-prefix ${errors.mrp ? 'error' : ''}`}
                        type="number" min="0"
                        value={form.mrp}
                        onChange={e => set('mrp', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    {errors.mrp && <p className="pf-error"><AlertCircle size={12} /> {errors.mrp}</p>}
                  </div>
                  <div className="pf-field">
                    <label className="pf-label">Sale Price *</label>
                    <div className="pf-input-prefix">
                      <span className="pf-prefix">₹</span>
                      <input
                        className={`pf-input pf-input-with-prefix ${errors.price ? 'error' : ''}`}
                        type="number" min="0"
                        value={form.price}
                        onChange={e => set('price', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    {errors.price && <p className="pf-error"><AlertCircle size={12} /> {errors.price}</p>}
                  </div>
                  <div className="pf-field">
                    <label className="pf-label">Discount %</label>
                    <div className={`pf-discount-badge ${discount > 0 ? 'active' : ''}`}>
                      {discount > 0 ? `${discount}% OFF` : '— %'}
                    </div>
                    <span className="pf-hint">Auto calculated</span>
                  </div>
                </div>

                {discount > 0 && (
                  <div className="pf-offer-preview">
                    <Tag size={14} />
                    <span>Sale badge will show: <strong>{discount}% OFF</strong> on the product card</span>
                  </div>
                )}

                <div className="pf-row" style={{ marginTop: '20px' }}>
                  <div className="pf-field">
                    <label className="pf-label">Offer Start Date</label>
                    <input type="date" className="pf-input" value={form.offerStart} onChange={e => set('offerStart', e.target.value)} />
                  </div>
                  <div className="pf-field">
                    <label className="pf-label">Offer End Date</label>
                    <input type="date" className="pf-input" value={form.offerEnd} onChange={e => set('offerEnd', e.target.value)} />
                  </div>
                </div>

                <div className="pf-field" style={{ marginTop: '8px' }}>
                  <label className="pf-label">Coupon Code (optional)</label>
                  <input
                    className="pf-input"
                    value={form.coupon || ''}
                    onChange={e => set('coupon', e.target.value.toUpperCase())}
                    placeholder="e.g. KOA10"
                    maxLength={20}
                  />
                  <span className="pf-hint">If set, customers must enter this code to get the discount</span>
                </div>

                <div className="pf-field" style={{ marginTop: '8px' }}>
                  <label className="pf-label">Sale Badge Text</label>
                  <div className="pf-select-wrap">
                    <select className="pf-input pf-select" value={form.badge || ''} onChange={e => set('badge', e.target.value)}>
                      <option value="">None</option>
                      <option value="Best Seller">Best Seller</option>
                      <option value="New">New</option>
                      <option value="Premium">Premium</option>
                      <option value="Popular">Popular</option>
                      <option value="Superfood">Superfood</option>
                      <option value="Limited">Limited</option>
                      <option value="Gift">Gift</option>
                    </select>
                    <ChevronDown size={14} className="pf-select-icon" />
                  </div>
                </div>
              </div>
            )}

            {/* ── SEO TAB ── */}
            {activeTab === 'seo' && (
              <div className="admin-card pf-section">
                <h3 className="pf-section-title">SEO Settings</h3>

                <div className="pf-field">
                  <label className="pf-label">Meta Title</label>
                  <input
                    className="pf-input"
                    value={form.metaTitle}
                    onChange={e => set('metaTitle', e.target.value)}
                    placeholder="Product name | KOA"
                    maxLength={70}
                  />
                  <span className={`pf-hint ${form.metaTitle.length > 60 ? 'pf-hint-warn' : ''}`}>
                    {form.metaTitle.length}/70 — Recommended: 50–60 chars
                  </span>
                </div>

                <div className="pf-field">
                  <label className="pf-label">Meta Description</label>
                  <textarea
                    className="pf-input pf-textarea"
                    value={form.metaDesc}
                    onChange={e => set('metaDesc', e.target.value)}
                    placeholder="Brief description for search engines (150–160 chars)..."
                    rows={3}
                    maxLength={165}
                  />
                  <span className={`pf-hint ${form.metaDesc.length > 155 ? 'pf-hint-warn' : ''}`}>
                    {form.metaDesc.length}/165 — Recommended: 150–160 chars
                  </span>
                </div>

                <div className="pf-field">
                  <label className="pf-label">URL Slug</label>
                  <div className="pf-slug-wrap">
                    <span className="pf-slug-prefix">KOA.com/product/</span>
                    <input
                      className="pf-input pf-slug-input"
                      value={form.slug}
                      onChange={e => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                      placeholder="product-url-slug"
                    />
                  </div>
                </div>

                <div className="pf-field">
                  <label className="pf-label">Default Image Alt Tag</label>
                  <input
                    className="pf-input"
                    value={form.altTag}
                    onChange={e => set('altTag', e.target.value)}
                    placeholder="e.g. Buy Premium California Almonds Online India"
                  />
                  <span className="pf-hint">Applied to featured image when no specific alt tag is set</span>
                </div>

                {/* SEO Preview */}
                <div className="pf-seo-preview">
                  <p className="pf-seo-preview-label">Google Preview</p>
                  <div className="pf-seo-preview-box">
                    <p className="pf-seo-url">KOA.com › product › {form.slug || 'product-name'}</p>
                    <p className="pf-seo-title">{form.metaTitle || form.name || 'Product Title | KOA'}</p>
                    <p className="pf-seo-desc">{form.metaDesc || form.description?.slice(0, 155) || 'Product description will appear here in search results...'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Side panel */}
          <div className="pf-sidebar">
            {/* Publish */}
            <div className="admin-card pf-side-card">
              <h4 className="pf-side-title">Publish</h4>
              <div className="pf-side-row">
                <span>Status</span>
                <span className={`status-badge status-${form.status}`}>{form.status}</span>
              </div>
              <div className="pf-side-row">
                <span>Stock</span>
                <span className={`status-badge status-${form.stock}`}>{form.stock === 'instock' ? 'In Stock' : 'Out of Stock'}</span>
              </div>
              {form.dateAdded && (
                <div className="pf-side-row">
                  <span>Date Added</span>
                  <span className="pf-side-val">{form.dateAdded}</span>
                </div>
              )}
              <button type="submit" form="product-form" className="admin-btn admin-btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: '16px' }} disabled={saving}>
                {saving ? '⏳ Saving...' : saved ? '✓ Saved!' : <><Save size={15} /> Save Product</>}
              </button>
            </div>

            {/* Price summary */}
            {(form.price || form.mrp) && (
              <div className="admin-card pf-side-card">
                <h4 className="pf-side-title">Price Summary</h4>
                <div className="pf-side-row">
                  <span>MRP</span>
                  <span className="pf-side-val">₹{form.mrp || '—'}</span>
                </div>
                <div className="pf-side-row">
                  <span>Sale Price</span>
                  <span style={{ color: '#D62828', fontWeight: 700 }}>₹{form.price || '—'}</span>
                </div>
                {discount > 0 && (
                  <div className="pf-side-row">
                    <span>You Save</span>
                    <span style={{ color: '#16803c', fontWeight: 700 }}>{discount}%</span>
                  </div>
                )}
              </div>
            )}

            {/* Image preview */}
            {form.images.length > 0 && (
              <div className="admin-card pf-side-card">
                <h4 className="pf-side-title">Featured Image</h4>
                <img
                  src={getFullUrl(form.images.find(i => i.featured)?.url || form.images[0].url)}
                  alt="Featured"
                  className="pf-featured-preview"
                />
                <p className="pf-hint" style={{ marginTop: '8px', textAlign: 'center' }}>{form.images.length} image{form.images.length > 1 ? 's' : ''} total</p>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
